import {
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Input,
  Link,
  Progress,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import IconBox from "components/icons/IconBox";
import { IconUpload } from "components/icons/Icons";
import alertTriangleIcon from "assets/img/customer/alert-triangle.svg";
import { QuestionOutlineIcon } from "@chakra-ui/icons";
import leftArrowIcon from "assets/img/customer/left-arrow.svg";
import deleteIcon from "assets/img/customer/delete-icon.svg";
import fileIcon from "assets/img/customer/file-icon.svg";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import StepShell from "../components/StepShell";
import { SUPPORT_LANGUAGES } from "../constants/languages";
import { useStep2Documents } from "../hooks/useStep2Documents";

// Per-failed-file inline UI. Mirrors the marketing-site card so the
// pattern is consistent across both checkouts: short explainer, a
// number input scoped to the right unit (words for Standard / pages
// for Certified), a primary "Use this estimate" submit, and a
// secondary "Get a free custom quote" escape. (PROD-2026-05-18-001
// follow-up.)
function ParseFailureCard(props: {
  documentId: string;
  unit: "words" | "pages";
  onManualEntry: (id: string, value: number) => void;
  onQuoteClick: () => void;
}) {
  const { documentId, unit, onManualEntry, onQuoteClick } = props;
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const placeholder = unit === "words" ? "e.g. 1200 words" : "e.g. 3 pages";
  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError(`Enter approximate ${unit}`);
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0) {
      setError("Enter a number greater than 0");
      return;
    }
    setError(null);
    onManualEntry(documentId, Math.round(n));
  };
  return (
    <Box
      mt="8px"
      p="12px"
      borderRadius="8px"
      border="1px solid #F2C94C"
      bg="#FFF8E1"
      color="#7A5B00"
    >
      <Text fontSize="13px" lineHeight="1.4">
        Enter the approximate {unit === "words" ? "word" : "page"} count so
        we can price this file. A Languex specialist verifies every estimate
        before your card is charged. Or convert this file to a free custom
        quote.
      </Text>
      <Flex
        mt="10px"
        gap="8px"
        direction={{ base: "column", sm: "row" }}
        alignItems={{ base: "stretch", sm: "center" }}
      >
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder}
          aria-label={`Approximate ${unit}`}
          bg="#FFF"
          borderColor="#E2C97A"
          _hover={{ borderColor: "#E2C97A" }}
          _focusVisible={{
            borderColor: "#2F80ED",
            boxShadow: "0 0 0 1px #2F80ED",
          }}
          flex="1"
          fontSize="13px"
          h="36px"
        />
        <Button
          size="sm"
          bg="#7A5B00"
          color="#FFF"
          _hover={{ bg: "#5C4500" }}
          _active={{ bg: "#5C4500" }}
          onClick={submit}
          fontSize="12px"
          fontWeight="600"
          h="36px"
        >
          Use this estimate
        </Button>
        <Button
          size="sm"
          variant="outline"
          borderColor="#E2C97A"
          color="#7A5B00"
          bg="#FFF"
          _hover={{ bg: "#FFF1C0" }}
          onClick={onQuoteClick}
          fontSize="12px"
          fontWeight="600"
          h="36px"
        >
          Get a free custom quote
        </Button>
      </Flex>
      {error && (
        <Text mt="6px" fontSize="12px" color="#C53030">
          {error}
        </Text>
      )}
    </Box>
  );
}

export default function Step2Documents(props: {
  onNext: () => void;
  onBack: () => void;
}) {
  const { onNext, onBack } = props;
  const navigate = useNavigate();
  const {
    uploadProject,
    isUploading,
    uploadError,
    totalDocs,
    uploadingFiles,
    sameLanguageError,
    languagePairError,
    canContinue,
    hasUnresolvedParseFailure,
    getRootProps,
    getInputProps,
    isDragActive,
    openPicker,
    onRemoveDocument,
    onManualEntry,
    onQuoteClick,
    onSourceLanguageChange,
    onTargetLanguageChange,
  } = useStep2Documents();

  const isStandard = uploadProject?.transitionType === "standard";
  const countUnit: "words" | "pages" = isStandard ? "words" : "pages";

  return (
    <StepShell
      step={2}
      title="Upload documents"
      description="Attach the documents you need translated."
      primaryLabel="Continue"
      onPrimary={onNext}
      secondaryLabel="Back"
      onSecondary={onBack}
      isPrimaryDisabled={!canContinue}
      // isPrimaryLoading={isUploading}
    >
      <Box
        borderRadius="12px"
        border="1px solid #E6ECF3"
        bg="#F5F7FA"
        p={{ base: "16px", md: "24px" }}
      >
        <Text fontSize="16px" fontWeight="600" color="#0B0E10">
          Attach your documents
        </Text>
        <Text mt="4px" fontSize="14px" color="#505E70">
          You can upload documents from your desktop, direct link, Facebook,
          Instagram, Flickr, Google Drive, Evernote, Box, One Drive, Dropbox.
        </Text>

        {uploadError ? (
          <Box
            mt="24px"
            p="16px"
            borderRadius="8px"
            fontWeight="400"
            bg="#FDECEC"
            color="#000000"
            fontSize="16px"
          >
            <Flex alignItems="center" gap="10px">
              <Image
                src={alertTriangleIcon}
                alt="alert"
                boxSize="24px"
                flexShrink={0}
              />
              <Box>{uploadError}</Box>
            </Flex>
          </Box>
        ) : null}

        <Box
          {...getRootProps({
            onClick: () => {
              openPicker();
            },
          })}
          mt="24px"
          borderRadius="12px"
          border="1px dashed #7C8FA3"
          bg="#FFF"
          padding={{ base: "18px", md: "22px" }}
          minH={{ base: "124px", md: "140px" }}
          cursor={isUploading ? "not-allowed" : "pointer"}
          opacity={isUploading ? 0.7 : 1}
          transition="all 0.2s"
        >
          <input {...getInputProps()} />
          <Flex direction="column" alignItems="center" justifyContent="center">
            <Box mb="10px" opacity={0.85}>
              <IconBox
                w="32px"
                h="32px"
                borderRadius="8px"
                bg="#F5F7FA"
                icon={<IconUpload w="24px" h="24px" />}
              />
            </Box>
            <Text fontSize="14px" color="#667085" textAlign="center">
              <Text as="span" color="#2F80ED" fontWeight="500">
                {isUploading ? "Uploading..." : "Click to upload"}
              </Text>{" "}
              <Text as="span" color="#0B0E10" fontSize="14px" fontWeight="400">
                {isDragActive ? "(drop now)" : "or drag and drop"}
              </Text>
            </Text>
            <Text mt="4px" fontSize="14px" color="#0B0E10" textAlign="center">
              PDF, DOC, DOCX, XLS, XLSX, TXT, CSV, PNG, JPG, JPEG, JSON
            </Text>
          </Flex>
        </Box>

        {totalDocs > 0 || uploadingFiles.length > 0 ? (
          <Box maxH="180px" overflowY="auto">
            {uploadingFiles.map((f) => (
              <Box
                key={f.id}
                p="12px"
                bg="#FFF"
                borderRadius="12px"
                border="1px solid #D2DAE5"
                mb="10px"
              >
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  mb="8px"
                >
                  <Flex minW="0" alignItems="center" gap="8px">
                    <Image
                      src={fileIcon}
                      alt="file"
                      boxSize="20px"
                      flexShrink={0}
                    />
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      color="#0B0E10"
                      noOfLines={1}
                    >
                      {f.name}
                    </Text>
                  </Flex>
                  <Text fontSize="12px" color="#667085" flexShrink={0}>
                    {f.progress >= 100 ? "Processing" : `${f.progress}%`}
                  </Text>
                </Flex>
                <Progress
                  value={Math.min(100, Math.max(0, f.progress))}
                  isIndeterminate={f.progress >= 100 && isUploading}
                  size="sm"
                  borderRadius="999px"
                  colorScheme="blue"
                  bg="#EEF2F6"
                />
              </Box>
            ))}

            {totalDocs > 0
              ? uploadProject.documents.map((d: any) => {
                  const parserFailed = d?.parserSuccess === false;
                  const isManual = d?.parserManual === true;
                  const countValue = isStandard ? d?.words ?? 0 : d?.pages ?? 0;
                  return (
                    <Box
                      mt="24px"
                      key={String(d.id)}
                      p="10px"
                      bg="#FFF"
                      borderRadius="12px"
                      border={
                        parserFailed
                          ? "1px solid #F2C94C"
                          : "1px solid #D2DAE5"
                      }
                    >
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Flex minW="0" alignItems="center" gap="8px">
                          <Image
                            src={fileIcon}
                            alt="file"
                            boxSize="20px"
                            flexShrink={0}
                          />
                          <Box minW="0">
                            <Text
                              fontSize="14px"
                              fontWeight="500"
                              color="#0B0E10"
                              noOfLines={1}
                            >
                              {d?.file?.name}
                            </Text>
                            {parserFailed ? (
                              <Text fontSize="13px" color="#7A5B00">
                                We couldn&apos;t auto-count this file.
                              </Text>
                            ) : (
                              <Text fontSize="14px" color="#0B0E10">
                                {isStandard ? "Word count" : "Pages"}:{" "}
                                {countValue}
                                {isManual && (
                                  <Text
                                    as="span"
                                    ml="6px"
                                    fontSize="12px"
                                    color="#7A5B00"
                                  >
                                    (manual estimate — verified before
                                    charge)
                                  </Text>
                                )}
                              </Text>
                            )}
                          </Box>
                        </Flex>
                        <IconButton
                          aria-label="Remove"
                          variant="ghost"
                          size="sm"
                          color="#2F80ED"
                          _hover={{ bg: "transparent" }}
                          _active={{ bg: "transparent" }}
                          _focus={{ boxShadow: "none" }}
                          icon={
                            <Image
                              src={deleteIcon}
                              alt="delete"
                              boxSize="20px"
                            />
                          }
                          onClick={() => {
                            onRemoveDocument(String(d.id));
                          }}
                        />
                      </Flex>
                      {parserFailed && (
                        <ParseFailureCard
                          documentId={String(d.id)}
                          unit={countUnit}
                          onManualEntry={onManualEntry}
                          onQuoteClick={onQuoteClick}
                        />
                      )}
                    </Box>
                  );
                })
              : null}

            {hasUnresolvedParseFailure && (
              <Flex
                mt="16px"
                p="10px 12px"
                gap="8px"
                alignItems="flex-start"
                borderRadius="8px"
                bg="#FFF8E1"
                border="1px solid #F2C94C"
              >
                <Spinner size="xs" color="#7A5B00" mt="3px" flexShrink={0} />
                <Text fontSize="12px" color="#7A5B00" lineHeight="1.4">
                  One or more files need an estimate before you can continue.
                  Enter the {countUnit === "words" ? "word" : "page"} count
                  above, or request a free custom quote.
                </Text>
              </Flex>
            )}
          </Box>
        ) : null}

        <Flex mt="16px" gap="8px" direction={{ base: "column", md: "row" }}>
          {/* !!! */}
          <Box flex="1">
            <Select
              placeholder="Choose language"
              value={uploadProject.sourceLanguage || ""}
              onChange={(e) => {
                onSourceLanguageChange(e.target.value);
              }}
              border="1px solid #7C8FA3"
              borderRadius="12px"
              height="48px"
              bg="#FFF"
              color="#0B0E10"
              _hover={{ borderColor: "#7C8FA3" }}
              _focusVisible={{
                borderColor: "#7C8FA3",
                boxShadow: "0 0 0 1px #7C8FA3",
              }}
            >
              {SUPPORT_LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Select>
          </Box>
          <Flex
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            display={{ base: "none", md: "flex" }}
          >
            <Image src={leftArrowIcon} alt="arrow" boxSize="24px" />
          </Flex>
          <Box flex="1">
            <Select
              placeholder="Choose language"
              value={uploadProject.targetLanguage?.[0] || ""}
              onChange={(e) => {
                onTargetLanguageChange(e.target.value);
              }}
              border="1px solid #7C8FA3"
              borderRadius="12px"
              height="48px"
              bg="#FFF"
              color="#0B0E10"
              _hover={{ borderColor: "#7C8FA3" }}
              _focusVisible={{
                borderColor: "#7C8FA3",
                boxShadow: "0 0 0 1px #7C8FA3",
              }}
            >
              {SUPPORT_LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Select>
          </Box>
        </Flex>

        {sameLanguageError ? (
          <Box mt="16px" p="16px" borderRadius="8px" fontWeight="400" bg="#FDECEC" color="#000000" fontSize="16px">
            <Flex alignItems="center" gap="10px">
              <Image src={alertTriangleIcon} alt="alert" boxSize="24px" flexShrink={0} />
              <Box>{sameLanguageError}</Box>
            </Flex>
          </Box>
        ) : null}

        {languagePairError ? (
          <Flex
            mt="24px"
            p="16px"
            bg="#EBF8FF"
            border="1px solid"
            borderColor="#90CDF4"
            borderRadius="12px"
            alignItems="center"
            gap="12px"
          >
            <QuestionOutlineIcon
              color="#2B6CB0"
              boxSize="24px"
              mt="2px"
              flexShrink={0}
            />
            <Box flex="1">
              <Text fontSize="13px" color="#1A365D" fontWeight="500">
                For non-English language pairs, please{" "}
                <Link
                  color="#2B6CB0"
                  fontWeight="600"
                  textDecoration="underline"
                  _hover={{ color: "#2C5282" }}
                  onClick={() => navigate("/customer/support")}
                >
                  request a custom quote
                </Link>
                .
              </Text>
            </Box>
          </Flex>
        ) : null}
      </Box>
    </StepShell>
  );
}
